import type { DayDataTypes } from 'src/api/models/dataList.model'
import {
  RatesDataGridContextState,
  RatesDataGridEditedDaysList,
  RowType
} from './RatesDataGridContext.types'

const getDirectChildIds = (
  rows: RatesDataGridContextState['rows'],
  parentRowId: string,
  type: RowType.CAPACITY | RowType.CHANNEL
) =>
  new Set(
    rows
      .filter(
        (row) =>
          row.fatherId === parentRowId &&
          (row.type === type ||
            (type === RowType.CAPACITY &&
              row.type === RowType.CAPACITY_SKELETON) ||
            (type === RowType.CHANNEL &&
              row.type === RowType.CHANNEL_SKELETON))
      )
      .map((row) => row.id)
  )

export const replaceChildRows = ({
  currentRows,
  parentRowId,
  type,
  childRows
}: {
  currentRows: RatesDataGridContextState['rows']
  parentRowId: string
  type: RowType.CAPACITY | RowType.CHANNEL
  childRows: RatesDataGridContextState['rows']
}) => {
  const parentIndex = currentRows.findIndex((row) => row.id === parentRowId)
  if (parentIndex < 0) return currentRows

  const parentRow = currentRows[parentIndex]
  const isOpen =
    type === RowType.CAPACITY
      ? 'showCapacities' in parentRow && parentRow.showCapacities
      : 'showChannels' in parentRow && parentRow.showChannels
  if (!isOpen) return currentRows

  const replacedChildIds = getDirectChildIds(currentRows, parentRowId, type)
  const rowsWithoutChildren = currentRows.filter(
    (row) =>
      !replacedChildIds.has(row.id) &&
      !(type === RowType.CAPACITY &&
        row.fatherId != null &&
        replacedChildIds.has(row.fatherId))
  )
  const updatedParentIndex = rowsWithoutChildren.findIndex(
    (row) => row.id === parentRowId
  )
  let insertionIndex = updatedParentIndex + 1

  if (type === RowType.CAPACITY) {
    while (
      rowsWithoutChildren[insertionIndex]?.fatherId === parentRowId &&
      rowsWithoutChildren[insertionIndex]?.type === RowType.CHANNEL
    ) {
      insertionIndex++
    }
  }

  return [
    ...rowsWithoutChildren.slice(0, insertionIndex),
    ...childRows,
    ...rowsWithoutChildren.slice(insertionIndex)
  ]
}

export const closeAndRemoveChildRows = ({
  currentRows,
  parentRowId,
  type
}: {
  currentRows: RatesDataGridContextState['rows']
  parentRowId: string
  type: RowType.CAPACITY | RowType.CHANNEL
}) => {
  const directChildIds = getDirectChildIds(currentRows, parentRowId, type)
  return currentRows
    .filter(
      (row) =>
        !directChildIds.has(row.id) &&
        !(type === RowType.CAPACITY &&
          row.fatherId != null &&
          directChildIds.has(row.fatherId))
    )
    .map((row) => {
      if (row.id !== parentRowId) return row
      if (type === RowType.CAPACITY && 'showCapacities' in row) {
        return { ...row, showCapacities: false }
      }
      if (type === RowType.CHANNEL && 'showChannels' in row) {
        return { ...row, showChannels: false }
      }
      return row
    })
}

export const changeRowsDataType = ({
  rows,
  roomTypeId,
  dataType
}: {
  rows: RatesDataGridContextState['rows']
  roomTypeId: string
  dataType: keyof DayDataTypes
}) =>
  rows.map((row) =>
    (row.type === RowType.CAPACITY ||
      row.type === RowType.RATE ||
      row.type === RowType.CHANNEL) &&
    row.data.roomTypeId === roomTypeId
      ? { ...row, dayDataType: dataType }
      : row
  )

export const discardRoomTypeEditedDays = (
  editedDaysList: RatesDataGridEditedDaysList,
  roomTypeId: string
) =>
  Object.entries(editedDaysList).reduce<RatesDataGridEditedDaysList>(
    (remainingEdits, [rowId, dateMap]) => {
      if (rowId.split('/')[1] !== roomTypeId) {
        remainingEdits[rowId] = dateMap
      }
      return remainingEdits
    },
    {}
  )

export const hasEditedDaysForRoomType = (
  editedDaysList: RatesDataGridEditedDaysList,
  roomTypeId: string
) =>
  Object.keys(editedDaysList).some(
    (rowId) => rowId.split('/')[1] === roomTypeId
  )

export const removeCommittedEdits = (
  currentEdits: RatesDataGridEditedDaysList,
  committedEdits: RatesDataGridEditedDaysList
) =>
  Object.entries(currentEdits).reduce<RatesDataGridEditedDaysList>(
    (remainingRows, [rowId, dateMap]) => {
      const remainingDates = Object.entries(dateMap).reduce<
        RatesDataGridEditedDaysList[string]
      >((dates, [date, value]) => {
        if (!committedEdits[rowId]?.[date]) dates[date] = value
        return dates
      }, {})
      if (Object.keys(remainingDates).length > 0) {
        remainingRows[rowId] = remainingDates
      }
      return remainingRows
    },
    {}
  )

export const commitEditsToRows = (
  rows: RatesDataGridContextState['rows'],
  committedEdits: RatesDataGridEditedDaysList
): RatesDataGridContextState['rows'] =>
  rows.map((row) => {
    const rowEdits = committedEdits[row.id]
    if (!rowEdits || !('data' in row) || !('dayDataByDate' in row.data)) {
      return row
    }
    const dayDataByDate = { ...row.data.dayDataByDate }
    Object.entries(rowEdits).forEach(([date, dayData]) => {
      dayDataByDate[date] = {
        ...(dayDataByDate[date] ?? { date }),
        ...dayData
      }
    })
    return {
      ...row,
      data: {
        ...row.data,
        dayDataByDate,
        dayDataList: Object.values(dayDataByDate)
      }
    } as RatesDataGridContextState['rows'][number]
  })
